import { Exception } from '@src/types';
import _ from 'lodash';

export class InputFieldValidationException extends Exception {}

export class InputFieldNotSetException extends InputFieldValidationException {}

/**
 *  The inputField object is meant to represent data that is provided by the user. Its main purpose is to provide a
 *  mechanism for sharing user data between controllers.
 *  The resolved data from the validator will be stored as the value.
 *
 *  @param validator - A function that is called to validate the input data. It should return a promise that resolves
 *              if the data is valid or reject with an Exception object if the data is invalid.
 */
export class InputField {
    private validator;
    private _trySetPromise;

    constructor(validator) {
        this.validator = validator;
    }

    /**
     *  Used to set the value for the input field. The provided value will be passed into the validator function
     *  for the InputField. If the validation is successful it will resolve the promise returned by this function
     *  with the data returned by the validator.
     *  If the validation fails then it will reject the promise returned by this
     *  function with the error thrown by the validator.
     *
     *  return Returns a promise that will resolve with the data returned
     *  by the validator if the validation is successful
     *      or will reject with the Exception from the validator if the value is invalid.
     */
    public trySetValue = (value) => {
        return this.validator(value)
            .then((finalValue) => {
                this._trySetPromise = Promise.resolve(finalValue);
                return this._trySetPromise;
            })
            .catch((e) => {
                return Promise.reject(new InputFieldNotSetException({ message: 'No value has been set yet.' }));
            });
    };

    public getValue = (value) => {
        if (this._trySetPromise) {
            return this._trySetPromise;
        } else {
            return Promise.reject(new InputFieldNotSetException({ message: 'No value has been set yet.' }));
        }
    };
}

export class InputFieldLoadingCache {
    private readonly _cache;
    constructor() {
        this._cache = {};
    }

    public get = (key, loadFunction) => {
        if (!this._cache.hasOwnProperty(key)) {
            this._cache[key] = loadFunction();
        }
        return this._cache[key];
    };
}

/**
 *  The InputFieldBuilder is used to build an InputField object and a validator function to be used by that InputField.
 *  The validator function is composed a series of validator functions provided by the client that are chained together
 *  in the order that they are provided. If any validator in the chain rejects then the entire validation will reject.
 *  If validation succeeds then the value stored in the InputField will be set to the value returned by the last
 *  validator in the chain.
 *  Each validator function in the chain will be passed two parameters: 1) the input value passed
 *  to the trySetValue() call of the final InputField, 2) A temporary loading cache that has a function get(key, loadFn)
 *  and is cleared between every call to trySetValue(). The temporary cache allows you to share data from service calls
 *  between your validators.
 *
 */
export class InputFieldBuilder {
    private _validatorChain;

    constructor() {
        this._validatorChain = [];
    }

    /**
     *  Adds a new validator function to the chain of validators to use when building the final validator for
     *  the inputField object. The function provided should take in two arguments. The first is the value being
     *  validated. The second is a loadingCache that is shared by all the validator functions in the chain and is
     *  reset between validation.
     */
    public addValidator(validator) {
        this._validatorChain.push(validator);
        return this;
    }

    /**
     *  Adds a new validator function to the front of the chain of validators to use
     *  when building the final validator for
     *  the inputField object. The function provided should take in two arguments. The first is the value being
     *  validated. The second is a loadingCache that is shared by all the validator functions in the chain and is
     *  reset between validation.
     */
    public addValidatorFront(validator) {
        this._validatorChain.unshift(validator);
        return this;
    }

    /**
     *  Builds a new InputField object and returns it.
     *
     *  @return Returns an InputField object
     */
    public build() {
        return new InputField(this._buildValidator());
    }

    /**
     *  Builds the single validator function that wraps all the validator functions that have been added to the
     *  chain. The function that it returns is responsible for creating a new LoadingCache instance each time it is
     *  called and calling each validator in the chain with the input value and the temporary cache instance.
     *
     *  return Returns a function that wraps all the validators in the chain.
     */
    private _buildValidator() {
        // create a new validator function to be used directly with an InputField
        return (value) => {
            // Create a new temporary LoadingCache for this run of the validator
            const tempCache = new InputFieldLoadingCache();

            // Create a promise chain that calls each validator in the ValidationChain in order
            let finalPromise = Promise.resolve(value);
            _.each(this._validatorChain, (validator) => {
                finalPromise = finalPromise.then(() => {
                    return validator(value, tempCache);
                });
            });
            return finalPromise;
        };
    }
}
